import { MaxChatMessage, MaxTurnState } from '../../types'

export type PendingTurnMergeSource = 'server' | 'cache'

export interface MergePendingTurnsOptions {
  source: PendingTurnMergeSource
  buildAssistantMessage: (turn: MaxTurnState) => MaxChatMessage | null
}

export interface MergePendingTurnsResult {
  messages: MaxChatMessage[]
  confirmedTurnIds: string[]
}

const cleanMessageId = (messageId: string | null | undefined): string | null => {
  if (!messageId) return null
  const cleaned = messageId.replace(/^MESSAGE#/, '').trim()
  return cleaned || null
}

const getMessageIds = (message: MaxChatMessage): string[] =>
  [cleanMessageId(message.messageId), cleanMessageId(message.id)].filter(
    (id): id is string => id !== null
  )

const getTurnAssistantId = (turn: MaxTurnState): string | null =>
  cleanMessageId(turn.assistantMessageId)

/**
 * Reconciles server/cache messages with the in-memory turns that may still be
 * ahead of the API. The input list is never mutated.
 */
export const mergePendingTurns = (
  list: MaxChatMessage[],
  turns: MaxTurnState[],
  options: MergePendingTurnsOptions
): MergePendingTurnsResult => {
  const messages = [...list]
  const confirmedTurnIds: string[] = []
  const presentMessageIndexes = new Map<string, number>()

  messages.forEach((message, index) => {
    getMessageIds(message).forEach((id) => {
      if (!presentMessageIndexes.has(id)) presentMessageIndexes.set(id, index)
    })
  })

  const pendingTurns: MaxTurnState[] = []

  ;[...turns]
    .sort((left, right) => left.startedAt - right.startedAt)
    .forEach((turn) => {
      const assistantId = getTurnAssistantId(turn)
      const existingIndex = assistantId ? presentMessageIndexes.get(assistantId) : undefined

      if (existingIndex !== undefined) {
        if (options.source === 'server') {
          confirmedTurnIds.push(turn.clientRequestId)
        } else {
          const assistantMessage = options.buildAssistantMessage(turn)
          if (assistantMessage) messages[existingIndex] = assistantMessage
        }
        return
      }

      pendingTurns.push(turn)
    })

  const unansweredUserIndexes: number[] = []
  let lastAssistantIndex = -1
  messages.forEach((message, index) => {
    if (message.role === 'assistant') lastAssistantIndex = index
  })
  messages.forEach((message, index) => {
    if (message.role === 'user' && index > lastAssistantIndex) {
      unansweredUserIndexes.push(index)
    }
  })

  const userOffset = Math.max(0, unansweredUserIndexes.length - pendingTurns.length)
  const pairedTurns = pendingTurns.map((turn, turnIndex) => ({
    turn,
    userIndex: unansweredUserIndexes[userOffset + turnIndex],
  }))

  const additions: MaxChatMessage[] = []
  const turnByUserIndex = new Map<number, MaxTurnState>()
  pairedTurns.forEach(({ turn, userIndex }) => {
    if (userIndex !== undefined) turnByUserIndex.set(userIndex, turn)
  })
  const originalMessages = [...messages]

  const appendTurn = (turn: MaxTurnState): void => {
    if (turn.userMessage) additions.push(turn.userMessage)
    const assistantMessage = options.buildAssistantMessage(turn)
    if (assistantMessage) additions.push(assistantMessage)
  }

  originalMessages.forEach((message, index) => {
    const turn = turnByUserIndex.get(index)
    if (!turn) return

    const assistantMessage = options.buildAssistantMessage(turn)
    if (assistantMessage) {
      const insertionIndex = messages.indexOf(message) + 1
      messages.splice(insertionIndex, 0, assistantMessage)
    }
  })

  // Appending after processing paired users preserves server ordering while
  // still keeping each unpaired turn's optimistic user and assistant together.
  pairedTurns.forEach(({ turn, userIndex }) => {
    if (userIndex === undefined) appendTurn(turn)
  })
  messages.push(...additions)

  return { messages, confirmedTurnIds }
}
